import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  Timestamp,
  limit
} from 'firebase/firestore';
import { db, retryOperation } from '../config';

const COLLECTION_NAME = 'locations';

export interface BusinessLocation {
  id?: string;
  name: string;
  type: 'Marina' | 'Store' | 'Kitchen' | 'Warehouse' | 'Office' | 'Other';
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone?: string;
  email?: string;
  website?: string;
  manager?: string;
  description?: string;
  primaryLocation: boolean;
  active: boolean;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  operatingHours?: {
    monday: { open: string; close: string } | null;
    tuesday: { open: string; close: string } | null;
    wednesday: { open: string; close: string } | null;
    thursday: { open: string; close: string } | null;
    friday: { open: string; close: string } | null;
    saturday: { open: string; close: string } | null;
    sunday: { open: string; close: string } | null;
  };
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

/**
 * Get all business locations
 */
export const getAllLocations = async (): Promise<BusinessLocation[]> => {
  try {
    return await retryOperation(async () => {
      const locationsQuery = query(
        collection(db, COLLECTION_NAME),
        orderBy('primaryLocation', 'desc'),
        orderBy('name')
      );
      
      const snapshot = await getDocs(locationsQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as BusinessLocation[];
    });
  } catch (error) {
    console.error('Error getting locations:', error);
    throw error;
  }
};

/**
 * Get active business locations only
 */
export const getActiveLocations = async (): Promise<BusinessLocation[]> => {
  try {
    return await retryOperation(async () => {
      const locationsQuery = query(
        collection(db, COLLECTION_NAME),
        where('active', '==', true),
        orderBy('primaryLocation', 'desc'),
        orderBy('name')
      );
      
      const snapshot = await getDocs(locationsQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as BusinessLocation[];
    });
  } catch (error) {
    console.error('Error getting active locations:', error);
    throw error;
  }
};

/**
 * Get a single business location by ID
 */
export const getLocationById = async (locationId: string): Promise<BusinessLocation | null> => {
  try {
    return await retryOperation(async () => {
      const locationDoc = await getDoc(doc(db, COLLECTION_NAME, locationId));
      
      if (!locationDoc.exists()) {
        return null;
      }
      
      const data = locationDoc.data();
      return {
        id: locationDoc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as BusinessLocation;
    });
  } catch (error) {
    console.error(`Error getting location with ID ${locationId}:`, error);
    throw error;
  }
};

/**
 * Create a new business location
 */
export const createLocation = async (locationData: Omit<BusinessLocation, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    return await retryOperation(async () => {
      // If this is set as primary location, we need to update any existing primary locations
      if (locationData.primaryLocation) {
        await updateExistingPrimaryLocation();
      }
      
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...locationData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    });
  } catch (error) {
    console.error('Error creating location:', error);
    throw error;
  }
};

/**
 * Update an existing business location
 */
export const updateLocation = async (locationId: string, locationData: Partial<BusinessLocation>): Promise<void> => {
  try {
    await retryOperation(async () => {
      // If this is being set as primary location, we need to update any existing primary locations
      if (locationData.primaryLocation) {
        await updateExistingPrimaryLocation();
      }
      
      const locationRef = doc(db, COLLECTION_NAME, locationId);
      
      await updateDoc(locationRef, {
        ...locationData,
        updatedAt: serverTimestamp()
      });
    });
  } catch (error) {
    console.error(`Error updating location with ID ${locationId}:`, error);
    throw error;
  }
};

/**
 * Delete a business location
 */
export const deleteLocation = async (locationId: string): Promise<void> => {
  try {
    await retryOperation(async () => {
      // Check if this is the primary location
      const locationDoc = await getDoc(doc(db, COLLECTION_NAME, locationId));
      
      if (locationDoc.exists() && locationDoc.data().primaryLocation) {
        throw new Error('Cannot delete the primary business location');
      }
      
      await deleteDoc(doc(db, COLLECTION_NAME, locationId));
    });
  } catch (error) {
    console.error(`Error deleting location with ID ${locationId}:`, error);
    throw error;
  }
};

/**
 * Helper function to update existing primary locations
 * when setting a new primary location
 */
const updateExistingPrimaryLocation = async (): Promise<void> => {
  const primaryLocationsQuery = query(
    collection(db, COLLECTION_NAME),
    where('primaryLocation', '==', true)
  );
  
  const snapshot = await getDocs(primaryLocationsQuery);
  
  const updatePromises = snapshot.docs.map(doc => 
    updateDoc(doc.ref, { 
      primaryLocation: false,
      updatedAt: serverTimestamp()
    })
  );
  
  await Promise.all(updatePromises);
};

/**
 * Initialize default locations if none exist
 */
export const initializeDefaultLocations = async (): Promise<void> => {
  try {
    const locationsQuery = query(collection(db, COLLECTION_NAME));
    const snapshot = await getDocs(locationsQuery);
    
    // If there are no locations, create default ones
    if (snapshot.empty) {
      await createLocation({
        name: 'Fallen Leaf Marina Main',
        type: 'Marina',
        address: '1234 Lake Shore Road',
        city: 'Fallen Leaf Lake',
        state: 'CA',
        zipCode: '96150',
        phone: '555-123-4567',
        email: 'info@fallenleafmarina.com',
        description: 'Main marina location with docks and rentals',
        primaryLocation: true,
        active: true
      });
      
      await createLocation({
        name: 'Fallen Leaf Marina Store',
        type: 'Store',
        address: '1234 Lake Shore Road',
        city: 'Fallen Leaf Lake',
        state: 'CA',
        zipCode: '96150',
        phone: '555-123-4568',
        email: 'store@fallenleafmarina.com',
        description: 'Marina store with supplies and merchandise',
        primaryLocation: false,
        active: true
      });
      
      await createLocation({
        name: 'Fallen Leaf Marina Kitchen',
        type: 'Kitchen',
        address: '1234 Lake Shore Road',
        city: 'Fallen Leaf Lake',
        state: 'CA',
        zipCode: '96150',
        phone: '555-123-4569',
        email: 'kitchen@fallenleafmarina.com',
        description: 'Marina restaurant and food service',
        primaryLocation: false,
        active: true
      });
      
      console.log('Default locations created successfully');
    }
  } catch (error) {
    console.error('Error initializing default locations:', error);
    throw error;
  }
};

/**
 * Get the primary business location
 */
export const getPrimaryLocation = async (): Promise<BusinessLocation | null> => {
  try {
    return await retryOperation(async () => {
      const primaryLocationQuery = query(
        collection(db, COLLECTION_NAME),
        where('primaryLocation', '==', true),
        limit(1)
      );
      
      const snapshot = await getDocs(primaryLocationQuery);
      
      if (snapshot.empty) {
        return null;
      }
      
      const doc = snapshot.docs[0];
      const data = doc.data();
      
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as BusinessLocation;
    });
  } catch (error) {
    console.error('Error getting primary location:', error);
    throw error;
  }
}; 